import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Check, Code2, Boxes, Cloud, Database, Search, Shield, Zap, Sparkles, Server, Smartphone, Cpu, Layers, Palette, Bot, Wrench, GitBranch, Brain, FileCode2 } from "lucide-react";
import { ALL_SERVICES, SERVICE_DETAILS } from "@/data/servicesDetail";
import { TechStackMarquee } from "@/components/TechStack";

// Slugs that have dedicated detail pages
const HAS_DETAIL = new Set(["web-development", "mobile-apps", "hosting", "blockchain", "seo"]);

const ICONS = {
  "web-development": Code2,
  "website-design": Palette,
  "custom-software": Layers,
  "api-development": GitBranch,
  "mobile-apps": Smartphone,
  "android-apps": Smartphone,
  "ios-apps": Smartphone,
  "hosting": Server,
  "vps-hosting": Database,
  "cloud-hosting": Cloud,
  "managed-servers": Shield,
  "linux-admin": Wrench,
  "devops": Cpu,
  "blockchain": Boxes,
  "smart-contracts": FileCode2,
  "tokens-nfts": Sparkles,
  "defi": Zap,
  "seo": Search,
  "automation": Bot,
  "ai-integration": Brain,
  "graphic-design": Palette,
  "templates": Sparkles,
  "it-consulting": Shield,
};

const GROUPS = ["Web", "Mobile", "Hosting", "Web3", "Growth", "Design", "Strategy"];

export default function Services() {
  const byGroup = (g) => ALL_SERVICES.filter((s) => s.group === g);
  const linkFor = (slug) => (HAS_DETAIL.has(slug) ? `/services/${slug}` : "/contact");

  return (
    <div data-testid="services-page">
      {/* HERO */}
      <section className="border-b border-[#1f1f22] relative overflow-hidden">
        <div className="glow-spot" style={{ width: 600, height: 600, background: "#DC1F33", top: "-200px", right: "-200px" }} />
        <div className="container-x py-24 md:py-32 relative">
          <div className="label-mono mb-6">// Services / Capabilities</div>
          <h1 className="font-display text-[clamp(40px,7vw,96px)] font-black uppercase tracking-tight leading-[0.92] text-white max-w-5xl">
            One partner.<br /><span className="text-[#DC1F33]">Every layer</span> of your stack.
          </h1>
          <p className="mt-8 text-lg text-zinc-400 max-w-2xl">
            Twenty-three end-to-end services across web, mobile, cloud, Web3, AI and growth — delivered by a single accountable team.
          </p>
        </div>
      </section>

      {/* BENTO BY GROUP */}
      <section className="border-b border-[#1f1f22]">
        <div className="container-x py-16">
          {GROUPS.map((g, gi) => {
            const items = byGroup(g);
            if (!items.length) return null;
            return (
              <div key={g} className={`${gi > 0 ? "mt-20" : ""}`}>
                <div className="flex items-end justify-between gap-6 mb-8">
                  <div>
                    <div className="label-mono mb-2">// {String(gi + 1).padStart(2, "0")} — {g}</div>
                    <h2 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight text-white leading-tight">
                      {g === "Web" && "Web platforms"}
                      {g === "Mobile" && "Mobile experiences"}
                      {g === "Hosting" && "Hosting & infrastructure"}
                      {g === "Web3" && "Blockchain & Web3"}
                      {g === "Growth" && "Growth & automation"}
                      {g === "Design" && "Brand & design"}
                      {g === "Strategy" && "Strategy"}
                    </h2>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border-l border-t border-[#1f1f22]">
                  {items.map((s) => {
                    const Icon = ICONS[s.slug] || Code2;
                    const has = HAS_DETAIL.has(s.slug);
                    return (
                      <Link
                        key={s.slug}
                        to={linkFor(s.slug)}
                        data-testid={`service-card-${s.slug}`}
                        className="grid-cell group"
                      >
                        <div className="flex items-start justify-between mb-8">
                          <div className="w-11 h-11 border border-[#27272A] flex items-center justify-center group-hover:border-[#DC1F33] group-hover:text-[#DC1F33] text-zinc-400 transition-colors">
                            <Icon size={18} />
                          </div>
                          <div className="label-mono">{s.code}</div>
                        </div>
                        <div className="font-display text-lg font-bold text-white mb-2 tracking-tight">{s.title}</div>
                        <div className="text-sm text-zinc-400 leading-relaxed">{s.short}</div>
                        <div className={`mt-6 flex items-center gap-2 label-mono text-[#DC1F33] ${has ? "opacity-100" : "opacity-0 group-hover:opacity-100"} transition-opacity`}>
                          {has ? <>Learn more <ArrowUpRight size={12} /></> : <>Discuss <ArrowUpRight size={12} /></>}
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* TECH STACK */}
      <section className="py-16 bg-[#0B0B0D] border-b border-[#1f1f22]">
        <div className="container-x mb-2">
          <div className="label-mono">// Modern technology stack</div>
        </div>
        <TechStackMarquee />
      </section>

      <section className="section">
        <div className="container-x">
          <div className="border border-[#27272A] p-12 md:p-16 bg-[#0E0E10] flex flex-col md:flex-row md:items-center justify-between gap-8">
            <div>
              <div className="label-mono mb-3">// Next step</div>
              <h3 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight text-white leading-tight">Not sure where to start?</h3>
              <p className="text-zinc-400 mt-3 max-w-xl">Tell us what you're trying to build — we'll send a free, candid recommendation within 24 hours.</p>
            </div>
            <Link to="/contact" data-testid="services-final-cta" className="btn-primary">
              Book Free Consult <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
